/**
 * Hunt service for CRUD operations on bottle_hunts
 */

import { supabase } from './supabase';

/**
 * Hunt service object with all database operations
 */
export const huntService = {
  /**
   * Fetches all hunts for the current user
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Hunt list
   */
  async getHunts({ page = 1, limit = 20, filters = {} } = {}) {
    const { data: { user } } = await supabase.auth.getUser();

    let query = supabase
      .from('bottle_hunts')
      .select('*, distilleries(id, distillery_name, country)', { count: 'exact' })
      .eq('user_id', user.id)
      .order('priority', { ascending: false })
      .order('created_at', { ascending: false });

    if (filters.search) {
      query = query.or(`bottle_name.ilike.%${filters.search}%,notes.ilike.%${filters.search}%`);
    }

    if (filters.type) {
      query = query.eq('type', filters.type);
    }

    if (filters.priority) {
      query = query.eq('priority', filters.priority);
    }

    if (limit !== 'All') {
      const from = (page - 1) * limit;
      query = query.range(from, from + limit - 1);
    }

    const { data, error, count } = await query;

    if (error) throw error;
    return { data: data || [], count: count || 0 };
  },

  /**
   * Fetches hunt entries for a specific user (shared view, read-only).
   * The caller must have been granted access via collection_shares (RLS enforced).
   * @param {string} userId - Owner's UUID
   * @param {Object} options - Optional filters
   * @returns {Promise<Array>}
   */
  async getHuntsForUser(userId, { page = 1, limit = 20, filters = {} } = {}) {
    let query = supabase
      .from('bottle_hunts')
      .select('*, distilleries(id, distillery_name, country)', { count: 'exact' })
      .eq('user_id', userId)
      .order('priority', { ascending: false })
      .order('created_at', { ascending: false });

    if (filters.search) {
      query = query.or(`bottle_name.ilike.%${filters.search}%,notes.ilike.%${filters.search}%`);
    }
    if (filters.type) {
      query = query.eq('type', filters.type);
    }

    if (limit !== 'All') {
      const from = (page - 1) * limit;
      query = query.range(from, from + limit - 1);
    }

    const { data, error, count } = await query;
    if (error) throw error;
    return { data: data || [], count: count || 0 };
  },

  /**
   * Creates a new hunt entry
   * @param {Object} hunt - Hunt data
   * @returns {Promise<Object>} Created hunt
   */
  async createHunt(hunt) {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('bottle_hunts')
      .insert({
        ...hunt,
        user_id: user.id,
        created_by: user.id
      })
      .select('*, distilleries(id, distillery_name, country)')
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Updates an existing hunt entry
   * @param {string} id - Hunt UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated hunt
   */
  async updateHunt(id, updates) {
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('bottle_hunts')
      .update({
        ...updates,
        updated_by: user.id
      })
      .eq('id', id)
      .select('*, distilleries(id, distillery_name, country)')
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Deletes a hunt entry
   * @param {string} id - Hunt UUID
   * @returns {Promise<void>}
   */
  async deleteHunt(id) {
    const { error } = await supabase
      .from('bottle_hunts')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};
